import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { RotateCcw, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import { contractsAPI } from '../lib/api';

/** Button + confirmation dialog: void a SIGNED letter and create a fresh draft that replaces it. */
export const ReissueLetterButton = ({ contract, onDone, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (contract.status !== 'signed') return null;

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await contractsAPI.reissue(contract.id, reason);
      toast.success('Signed letter voided — a new draft is ready to send');
      setOpen(false); setReason('');
      onDone?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-issue failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button" size="sm" variant="ghost"
        data-testid={`reissue-${contract.id}`}
        className={`h-7 text-xs text-amber-700 hover:bg-amber-50 ${className}`}
        title="Void this signed letter and generate a new draft"
        onClick={() => setOpen(true)}
      >
        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Void & re-issue
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="reissue-dialog">
          <DialogHeader>
            <DialogTitle>Void & re-issue this signed letter?</DialogTitle>
            <DialogDescription>
              The letter signed{contract.signed_at ? ` on ${new Date(contract.signed_at).toLocaleDateString()}` : ''}{contract.signer_name ? ` by ${contract.signer_name}` : ''} is marked
              <strong> superseded</strong> (its signature and audit trail are kept) and a fresh draft with the current students and phases is created. The new draft must be sent and signed again.
            </DialogDescription>
          </DialogHeader>
          <label className="text-xs font-medium text-gray-700">
            Reason (recorded in the audit trail)
            <Textarea
              data-testid="reissue-reason"
              className="mt-1"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Placement phases added to the cycle; students changed"
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="reissue-confirm" onClick={confirm} disabled={busy || reason.trim().length < 3}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />} Void & create new draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Small provenance line for letters that replace / were replaced by another. */
export const ReissueNote = ({ contract }) => {
  if (contract.replaces) {
    return (
      <p data-testid={`replaces-note-${contract.id}`} className="text-[11px] text-amber-700 inline-flex items-center gap-1 mt-1">
        <History className="w-3 h-3" /> Replaces letter signed {contract.replaces.signed_at ? new Date(contract.replaces.signed_at).toLocaleDateString() : ''}{contract.replaces.signer_name ? ` by ${contract.replaces.signer_name}` : ''}
      </p>
    );
  }
  if (contract.superseded_by) {
    return (
      <p data-testid={`superseded-note-${contract.id}`} className="text-[11px] text-gray-500 inline-flex items-center gap-1 mt-1">
        <History className="w-3 h-3" /> Superseded — re-issued{contract.void_reason ? `: ${contract.void_reason}` : ''}
      </p>
    );
  }
  return null;
};

export default ReissueLetterButton;
